<script lang="ts">
  /** AN NPC'S PROFILE (Keys's window, frames 2–3): its face between its slots, its CLASS, its code and version — and its DB: the file grid
   *  (the stock's one Logic file, its memory, one file per change since) and the folder tree. A stock NPC shows one file; an NPC that
   *  has been changed (Guy) shows its folders full. Opening a file opens its change window; OPEN ALL opens every one (frames 4–6). */
  import { driveSlots, fileOf, folderOfEntry, ground, npcCode, pad, portrait, stateSlots } from './codes'
  import { KIND_LABEL, RELATION_LABEL, type Entry, type Rec } from './types'

  let { rec, index, onclose, onentry, onall, onlogic, onmemory, onrole }: {
    rec: Rec; index: number; onclose: () => void; onentry: (e: Entry) => void; onall: () => void; onlogic: () => void; onmemory: () => void; onrole: () => void
  } = $props()

  const FOLDERS = ['Blueprint', 'Character_Fn', 'NPC_Tree', 'Additional_Entries', 'Readout'] as const
  let folder = $state<string | null>(null)
  let query = $state('')
  const left = $derived(driveSlots(rec.effective.drives))
  const right = $derived(stateSlots(rec))
  const counts = $derived(Object.fromEntries(FOLDERS.map((f) => [f, rec.journal.filter((e) => folderOfEntry(e) === f).length
    + (f === 'Additional_Entries' && rec.memories.count ? 1 : 0) + (f === 'NPC_Tree' ? 1 : 0)])))   // its Logic file lives in NPC_Tree
  const entries = $derived(rec.journal.filter((e) => (!folder || folderOfEntry(e) === folder) && (!query || `${e.fn} ${KIND_LABEL[e.kind]}`.toLowerCase().includes(query.toLowerCase()))))
  const showLogic = $derived(!folder || folder === 'NPC_Tree')
  const showMemory = $derived(rec.memories.count > 0 && (!folder || folder === 'Additional_Entries'))
  const items = $derived(entries.length + (showLogic ? 1 : 0) + (showMemory ? 1 : 0))
  const code = $derived(rec.stock ? 'STOCK' : npcCode(rec.tenant))
</script>

<section class="profile frame" aria-label={`${rec.title} — profile`}>
  <header>
    <span class="tools" aria-hidden="true">
      {#each ['◧', '◂', '▸', '⟳', '▣', '⬚', '⤢', '▤', '◎'] as t}<i>{t}</i>{/each}
    </span>
    <span class="crumb mono">{code} ▸ {folder ?? 'Main PC'}</span>
    <input class="find" placeholder="⌕ All" bind:value={query} aria-label="Find in this NPC's files" />
    {#if rec.journal.length}<button class="btn" onclick={onall}>Open all ({rec.journal.length})</button>{/if}
    <button class="x" onclick={onclose} aria-label="Close the profile">✕</button>
  </header>
  <div class="body">
    <div class="id">
      <ul class="slots">{#each left as s}<li class:on={s.lit} title={`${s.label} ${s.sub}`}><b>{s.code}</b><small>{s.label}</small></li>{/each}</ul>
      <div class="face halftone" style={ground(index)}><img src={portrait(rec.effective.body)} alt={`${rec.title}'s face`} /></div>
      <ul class="slots">{#each right as s}<li class:on={s.lit} title={`${s.label} ${s.sub}`}><b>{s.code}</b><small>{s.label}</small></li>{/each}</ul>
      <div class="plate">
        <span class="cls caps">{rec.record.class}</span>
        <span class="name">{rec.title}{#if !rec.stock}<em>{RELATION_LABEL[rec.relation]}</em>{/if}</span>
      </div>
      <div class="ver">
        <b>R{pad(rec.record.revision)}</b>
        <span class="mono">{code}<br />VERSION {rec.record.template.version ?? '1.0.0'}</span>
      </div>
    </div>
    <div class="files scroll">
      {#if showLogic}<button class="file" onclick={onlogic} title="The class it was stamped from (its graph + role)"><i class="ico logic"></i><span>Logic</span></button>{/if}
      {#if showMemory}<button class="file" onclick={onmemory} title="Its NEXUS memory"><i class="ico mem"></i><span>memory.db</span></button>{/if}
      {#each entries as e (e.id)}
        {@const f = fileOf(e)}
        <button class="file" onclick={() => onentry(e)} title={`${KIND_LABEL[e.kind]} · ${e.fn} · revision ${e.revision}`}><i class={`ico ${e.kind}`}></i><span>{f.name}<small>.{f.ext}</small></span></button>
      {/each}
    </div>
    <nav class="tree mono" aria-label="Folders">
      <button class:sel={!folder} onclick={() => (folder = null)}>$PROJECT</button>
      <button class="sub" class:sel={false} onclick={onrole}>_RunsAs.role</button>
      {#each FOLDERS as f}
        <button class="sub" class:sel={folder === f} class:empty={!counts[f]} onclick={() => (folder = folder === f ? null : f)}>{f}<span>{counts[f] || ''}</span></button>
      {/each}
    </nav>
  </div>
  <footer class="mono"><span>{items} {items === 1 ? 'item' : 'items'}</span><span class="muted">{rec.stock ? 'a stock record — never changed' : `${rec.journal.length} changes since it was stamped`}</span></footer>
</section>

<style>
  .profile { background: rgba(9, 12, 16, .97); display: flex; flex-direction: column; min-height: 0; max-height: 100%; }
  header { display: flex; align-items: center; gap: 10px; padding: 6px 10px; border-bottom: 1px solid var(--line-dim); }
  .tools { display: flex; gap: 10px; color: var(--muted); font-style: normal; }
  .tools i { font-style: normal; }
  .crumb { color: var(--muted); margin-left: auto; }
  .find { width: 130px; padding: 3px 8px; font-size: 13px; }
  .x { color: var(--muted); font-size: 16px; padding: 0 4px; }
  .x:hover { color: var(--text); }
  .body { display: grid; grid-template-columns: minmax(300px, 1.05fr) 1.3fr 170px; gap: 14px; padding: 12px; min-height: 0; flex: 1; }
  .id { display: grid; grid-template-columns: 44px 1fr 44px; grid-template-rows: 1fr auto; gap: 6px; align-items: stretch; }
  .slots { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
  .slots li { flex: 1; border: 1px solid #263341; background: #0f141a; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--faint); min-height: 46px; }
  .slots li b { font: 600 15px var(--display); }
  .slots li small { font-size: 9px; text-transform: uppercase; letter-spacing: .06em; }
  .slots li.on { border-color: var(--cyan); color: var(--cyan); box-shadow: 0 0 10px rgba(34, 211, 238, .2) inset; }
  .face { aspect-ratio: 1; border: 1px solid var(--line-dim); overflow: hidden; }
  .face img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .plate { grid-column: 1 / 3; display: flex; flex-direction: column; justify-content: flex-end; }
  .cls { font-weight: 700; font-size: 34px; color: var(--cyan); line-height: .95; }
  .name { color: var(--text); font-size: 15px; display: flex; gap: 8px; align-items: baseline; }
  .name em { font-style: normal; font-size: 11px; letter-spacing: .08em; color: var(--amber); }
  .ver { display: flex; flex-direction: column; align-items: flex-end; justify-content: flex-end; text-align: right; }
  .ver b { font: 600 30px var(--display); color: var(--cyan); line-height: 1; }
  .ver span { font-size: 9.5px; color: var(--muted); }
  .files { display: grid; grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); gap: 10px 8px; align-content: start; overflow: auto; max-height: 420px; padding-right: 4px; }
  .file { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 4px 2px; color: var(--muted); font-size: 12px; text-align: center; }
  .file:hover { background: rgba(34, 211, 238, .08); color: var(--text); }
  .file span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .file small { color: var(--faint); }
  .ico { width: 30px; height: 38px; background: linear-gradient(160deg, #e9eef2, #aab6c0); clip-path: polygon(0 0, 72% 0, 100% 24%, 100% 100%, 0 100%); position: relative; }
  .ico::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 5px; }
  .ico.logic::after { background: var(--cyan); } .ico.tree::after { background: var(--green); } .ico.input::after { background: var(--amber); }
  .ico.entry::after { background: #a78bfa; } .ico.readout::after { background: var(--red); } .ico.mem::after { background: #f472b6; }
  .tree { display: flex; flex-direction: column; gap: 2px; border-left: 1px solid var(--line-dim); padding-left: 8px; }
  .tree button { text-align: left; color: var(--muted); padding: 2px 4px; display: flex; justify-content: space-between; }
  .tree button.sub { padding-left: 14px; }
  .tree button.sel { color: var(--text); font-weight: 500; background: rgba(34, 211, 238, .1); }
  .tree button.empty { color: var(--faint); }
  .tree button span { color: var(--cyan); }
  footer { display: flex; gap: 14px; padding: 5px 10px; border-top: 1px solid var(--line-dim); color: var(--muted); }
  @media (max-width: 900px) {
    .body { grid-template-columns: 1fr; }
    .tree { border-left: 0; flex-direction: row; flex-wrap: wrap; padding: 0; }
    .tools, .crumb { display: none; }
    .cls { font-size: 28px; }
  }
</style>
